import { Button, Flex, IconFont, Select, Text } from '@byecode/ui'
import type { CustomStatisticField, DataSourceAbstract } from '@lighthouse/core'
import {
    canSelectAsStatisticKeyField,
    FieldTypeTag,
    getAppointField,
    getFieldIcon,
    Group4ByecodeUi,
    ListItem4ByecodeUi
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { StatisticFieldItem } from './StatisticFieldItem'

interface CustomStatisticSettingProps {
    dataSource: DataSourceAbstract
}

export const CustomStatisticSetting: React.FC<CustomStatisticSettingProps> = ({ dataSource }) => {
    const { control, setValue, getValues } = useFormContext()
    const disabledWithVersion = useIsDisabledWithVersion()
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'customStatistic.fields',
        keyName: 'fieldKey'
    })

    const [openFilterIndex, setOpenFilterIndex] = useState<number | null>(null)

    const uniqueIdOptions = useMemo(
        () =>
            Object.values(dataSource.schema)
                .filter(canSelectAsStatisticKeyField)
                .map(field => ({
                    label: field.name,
                    value: field.id,
                    icon: getFieldIcon(field.id, field.type, field.innerType),
                    extra: <FieldTypeTag type={field.type} innerType={field.innerType} />
                })),
        [dataSource.schema]
    )

    useEffect(() => {
        const idField = getAppointField(dataSource, 'ID')
        const current = getValues('customStatistic.uniqueIdField') as string | undefined
        if (!current || !dataSource.schema[current]) {
            setValue('customStatistic.uniqueIdField', idField?.id)
        }
    }, [dataSource, getValues, setValue])

    const handleAdd = useCallback(() => {
        const nextIndex = fields.length + 1
        const next: CustomStatisticField = {
            id: `stat_${nanoid(10)}`,
            name: `统计${nextIndex}`,
            dsId: '',
            targetFieldId: ''
        }
        append(next)
    }, [append, fields.length])

    return (
        <Group4ByecodeUi label="自定义统计字段">
            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>唯一 id</Text>
                <Controller
                    control={control}
                    name="customStatistic.uniqueIdField"
                    render={({ field }) => (
                        <Select
                            style={{ width: 180 }}
                            placeholder="请选择字段"
                            options={uniqueIdOptions}
                            value={field.value}
                            onChange={field.onChange}
                            searchable
                            disabled={disabledWithVersion}
                        />
                    )}
                />
            </ListItem4ByecodeUi>

            <Flex direction="column" gap={8} px={8} py={4}>
                {fields.map((item, index) => (
                    <StatisticFieldItem
                        key={item.fieldKey}
                        prefixName={`customStatistic.fields.${index}`}
                        viewDataSource={dataSource}
                        filterMenuOpened={openFilterIndex === index}
                        onFilterMenuOpenedChange={opened => setOpenFilterIndex(opened ? index : null)}
                        onRemove={() => remove(index)}
                    />
                ))}
                <Button
                    radius={100}
                    onClick={handleAdd}
                    disabled={disabledWithVersion}
                    icon={<IconFont type="Add" fill="var(--color-gray-500)" size={16} />}
                >
                    添加统计字段
                </Button>
            </Flex>
        </Group4ByecodeUi>
    )
}
